import { useMemo, useState } from 'react'
import type { ProjectSummary } from '../lib/db'
import { money } from '../lib/paper'
import {
  allocationProblem,
  byCategory,
  CATEGORIES,
  jobResult,
  loadExpenses,
  missingActuals,
  newExpense,
  NOT_IN_COSTS,
  overhead,
  saveExpenses,
  spentOn,
  type Category,
  type Expense,
} from '../lib/expenses'
import { Empty, NotKnown, Sheet } from './bits'

/**
 * WHAT THE SEASON COST, AND WHAT IS STILL UNKNOWN.
 *
 * EXP-01 and EXP-02. Two halves: the costs somebody entered, and what they say
 * about the jobs they were spent on.
 *
 * THE SECOND HALF IS THE DANGEROUS ONE. A job with a three thousand dollar
 * quote and nothing recorded against it shows a hundred percent margin on
 * every naive version of this screen, and that is the number a person prices
 * next season off. Here it reads no costs recorded, and the count of jobs in
 * that state is printed at the top, because the useful thing about this screen
 * on day one is knowing how much of it is still blank.
 */
/** A new, empty cost dated today. Named once so the two ways in agree. */
const freshCost = () =>
  newExpense(`ex_${Date.now().toString(36)}`, new Date().toISOString().slice(0, 10))


export default function Costs({ items }: { items: ProjectSummary[] | null }) {
  const [list, setList] = useState<Expense[]>(() => loadExpenses())

  const [editing, setEditing] = useState<Expense | null>(null)

  const jobs = useMemo(() => items ?? [], [items])
  const write = (next: Expense[]) => {
    setList(next)
    saveExpenses(next)
  }

  const total = list.reduce((n, e) => n + e.total, 0)
  const cats = byCategory(list)
  const over = overhead(list)

  const results = useMemo(
    () =>
      jobs
        .filter((j) => (j.money?.total ?? 0) > 0)
        .map((j) => ({ job: j, r: jobResult(j.id, j.money?.total ?? 0, list) }))
        .sort((a, b) => {
          // Known results first, worst margin at the top: EXP-02 asks for the
          // largest variance, and the largest variance is the one losing money.
          if ((a.r.margin === null) !== (b.r.margin === null)) return a.r.margin === null ? 1 : -1
          return (a.r.margin ?? 0) - (b.r.margin ?? 0)
        }),
    [jobs, list],
  )
  const blank = missingActuals(results.map((x) => x.r))

  const who = (j: ProjectSummary) => j.customer.trim() || j.address.trim() || j.name

  return (
    <>
      <div className="ws-head">
        <div>
          <h1>Costs</h1>
          <p>What went out, and what it says about the jobs it went on.</p>
        </div>
        <div className="ws-head-acts">
          <button
            className="btn primary"
            onClick={() => setEditing(freshCost())}

          >
            Record a cost
          </button>
        </div>
      </div>

      {list.length > 0 && (
        <p className="est-answer">
          <b>{money(total)}</b> recorded across {list.length}{' '}
          {list.length === 1 ? 'cost' : 'costs'}.
          {over > 0 && <> {money(over)} of it is the business rather than a house.</>}
        </p>
      )}

      {/* ----------------------------------------------- what it says --- */}

      <div className="ws-sec-head">
        <h2>Job by job</h2>
        <p>
          What the work is worth against what has actually been entered against it. Not what has
          been collected: that is on the invoices.
        </p>
      </div>

      {results.length === 0 ? (
        <div className="ws-card">
          <Empty title="Nothing priced yet">
            There is nothing to compare a cost against until a job has a price on it.
          </Empty>
        </div>
      ) : (
        <div className="ws-card est-list">
          <div className="ws-thead cost-head" aria-hidden="true">
            <span>Job</span>
            <span>Worth</span>
            <span>Cost</span>
            <span>Left</span>
          </div>
          {results.map(({ job, r }) => (
            <div key={job.id} className="cost-row">
              <span className="ws-name">
                <strong>{who(job)}</strong>
                {job.money?.number && <span>{job.money.number}</span>}
              </span>
              <span className="cost-n worth">{money(r.worth)}</span>
              <span className="cost-n spent">
                {r.margin === null ? <em>none entered</em> : money(r.cost)}
              </span>
              {/*
                NO NUMBER AT ALL WHERE NOTHING IS KNOWN. A margin computed off
                zero costs is a hundred percent, and a hundred percent is the
                figure somebody prices next season off.
              */}
              <span className={`cost-n cost-margin${(r.margin ?? 0) < 0 ? ' bad' : ''}`}>
                {r.margin === null ? (
                  <em>unknown</em>
                ) : (
                  <>
                    {money(r.margin)}
                    <i>{r.marginPct}%</i>
                  </>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {blank > 0 && (
        <p className="est-unknown">
          <b>
            {blank} of these {results.length} {blank === 1 ? 'job has' : 'jobs have'} nothing
            recorded against {blank === 1 ? 'it' : 'them'}.
          </b>{' '}
          They are shown as unknown rather than as fully profitable, because a margin worked out
          against no costs is always a hundred percent and is never true.
        </p>
      )}

      {/* -------------------------------------------------- the costs --- */}

      <div className="ws-sec-head">
        <h2>What went out</h2>
        <p>Newest first. A cost can be split across houses, and the split has to add up.</p>
      </div>

      {cats.length > 0 && (
        <div className="cost-cats">
          {cats.map((c) => (
            <span key={c.category}>
              <b>{money(c.total)}</b>
              {c.label}
            </span>
          ))}
        </div>
      )}

      {list.length === 0 ? (
        <div className="ws-card">
          <Empty
            title="Nothing recorded yet"
            action={{ label: 'Record a cost', onClick: () => setEditing(freshCost()) }}
          >
            A cost is a day, a vendor, an amount, and where it went.
          </Empty>
        </div>
      ) : (
        <div className="ws-card est-list">
          {[...list]
            .sort((a, b) => (a.day < b.day ? 1 : -1))
            .map((e) => (
              <button key={e.id} className="cost-item" onClick={() => setEditing(e)}>
                <span className="ws-name">
                  <strong>{e.vendor.trim() || 'No vendor named'}</strong>
                  <span>
                    {[
                      e.day,
                      CATEGORIES.find((c) => c.id === e.category)?.label,
                      e.allocations.length > 1
                        ? `split across ${e.allocations.length}`
                        : e.allocations[0]?.jobId
                          ? who(jobs.find((j) => j.id === e.allocations[0].jobId) ?? ({ customer: '', address: '', name: 'a job' } as ProjectSummary))
                          : 'the business',
                    ]
                      .filter(Boolean)
                      .join('  ·  ')}
                  </span>
                </span>
                <span className="cost-n">{money(e.total)}</span>
              </button>
            ))}
        </div>
      )}

      <NotKnown title="What this does not cost" items={NOT_IN_COSTS} />

      {editing && (
        <CostSheet
          expense={editing}
          jobs={jobs}
          onClose={() => setEditing(null)}
          onDelete={() => {
            write(list.filter((e) => e.id !== editing.id))
            setEditing(null)
          }}
          onSave={(next) => {
            write(
              list.some((e) => e.id === next.id)
                ? list.map((e) => (e.id === next.id ? next : e))
                : [...list, next],
            )
            setEditing(null)
          }}
        />
      )}
    </>
  )
}

/**
 * ONE COST.
 *
 * The split is checked as it is typed rather than on the way out, because the
 * number somebody is about to change is the one they can still fix, and it
 * says which way it is wrong: sixty dollars over and two hundred short are
 * different mistakes.
 */
function CostSheet({
  expense,
  jobs,
  onClose,
  onSave,
  onDelete,
}: {
  expense: Expense
  jobs: ProjectSummary[]
  onClose: () => void
  onSave: (e: Expense) => void
  onDelete: () => void
}) {
  const [e, setE] = useState(expense)
  const bad = allocationProblem(e)
  /*
   * A BLANK FORM IS NOT A MISTAKE.
   *
   * This sheet opened on a new cost and immediately printed "the total is not
   * an amount above zero" in alarm red, about a field nobody had reached yet.
   * The Save button being off already says the form is not finished; the red
   * line is for something a person got wrong, and on a sheet they just opened
   * they have not got anything wrong. It appears once they have put a number
   * in, which is the first moment there is an opinion to have.
   */
  const started = e.total > 0 || !!e.vendor.trim()

  const who = (j: ProjectSummary) => j.customer.trim() || j.address.trim() || j.name

  const setAlloc = (i: number, p: Partial<Expense['allocations'][number]>) =>
    setE({ ...e, allocations: e.allocations.map((a, n) => (n === i ? { ...a, ...p } : a)) })

  return (
    <Sheet
      title={expense.vendor.trim() || 'A cost'}
      variant="vsheet"
      onClose={onClose}
    >
      <div className="vsheet-body">
        <div className="ws-form vsheet-form">
          <label>
            <span>The day on the receipt</span>
            <input type="date" value={e.day} onChange={(ev) => setE({ ...e, day: ev.target.value })} />
          </label>
          <label>
            <span>Total</span>
            <input
              type="number"
              min={0}
              step="0.01"
              value={e.total || ''}
              onChange={(ev) => setE({ ...e, total: Number(ev.target.value) })}
            />
          </label>
          <label>
            <span>Who from</span>
            <input
              value={e.vendor}
              placeholder="The supplier"
              onChange={(ev) => setE({ ...e, vendor: ev.target.value })}
            />
          </label>
          <label>
            <span>What kind</span>
            <select
              value={e.category}
              onChange={(ev) => setE({ ...e, category: ev.target.value as Category })}
            >
              {CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <label className="ws-wide">
            <span>Anything worth remembering</span>
            <input
              value={e.note}
              placeholder="Optional"
              onChange={(ev) => setE({ ...e, note: ev.target.value })}
            />
          </label>
        </div>

        {/*
          WHERE IT WENT. A five hundred dollar bulb order across four houses
          is the ordinary case in this trade, and a split that does not add
          up is a cost that has quietly vanished or been counted twice.
        */}
        <div className="alloc">
          <p className="ws-eyebrow">Where it went</p>
          {e.allocations.map((a, i) => (
            <div key={i} className="alloc-row">
              <select
                aria-label="Which job"
                value={a.jobId}
                onChange={(ev) => setAlloc(i, { jobId: ev.target.value })}
              >
                <option value="">The business, not one house</option>
                {jobs.map((j) => (
                  <option key={j.id} value={j.id}>
                    {who(j)}
                  </option>
                ))}
              </select>
              <input
                aria-label="How much"
                type="number"
                min={0}
                step="0.01"
                value={a.amount || ''}
                onChange={(ev) => setAlloc(i, { amount: Number(ev.target.value) })}
              />
              {e.allocations.length > 1 && (
                <button
                  className="quiet-drop"
                  onClick={() => setE({ ...e, allocations: e.allocations.filter((_, n) => n !== i) })}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          <div className="alloc-acts">
            <button
              className="btn"
              onClick={() => setE({ ...e, allocations: [...e.allocations, { jobId: '', amount: 0 }] })}
            >
              Split it again
            </button>
            {/* One press for the ordinary case, which is the whole thing on
                one house or the whole thing on the business. */}
            {e.allocations.length === 1 && e.total > 0 && e.allocations[0].amount !== e.total && (
              <button className="link-btn" onClick={() => setAlloc(0, { amount: e.total })}>
                Put the whole {money(e.total)} here
              </button>
            )}
          </div>
          {bad && started && <p className="set-bad">{bad}</p>}

        </div>
      </div>

      <div className="ws-actions">
        <button className="vsheet-drop" onClick={onDelete}>
          Remove
        </button>
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary" disabled={!!bad} onClick={() => onSave(e)}>
          Save
        </button>
      </div>
    </Sheet>
  )
}
